'use client';

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import SettingsSidebar, { SettingsTab } from './components/SettingsSidebar';
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SettingsPage() {
    const { user, userProfile, updateProfile, deleteAccount, signOut } = useAuth();
    const router = useRouter();

    const [activeTab, setActiveTab] = useState<SettingsTab>('general');

    const handleSignOut = async () => {
        const { error } = await signOut();
        if (error) {
            toast.error(`Failed to sign out: ${error.message}. Please try again.`);
        } else {
            toast.success('Successfully signed out');
            router.push('/login');
        }
    };

    const handleDeleteAccount = async () => {
        const { error } = await deleteAccount();
        if (!error) {
            router.push('/');
            return { error: null };
        }
        return { error };
    };

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="mb-8">
                    <h1 className="text-3xl font-headline font-bold text-gray-900">Account Settings</h1>
                    <p className="text-gray-600 mt-2 text-lg">Manage your personal information and account preferences.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-3">
                        <SettingsSidebar activeTab={activeTab} onTabChange={setActiveTab} />
                    </div>

                    {/* Content Area */}
                    <div className="lg:col-span-9 space-y-6">
                        {activeTab === 'general' && (
                            <ProfileSection
                                user={user}
                                userProfile={userProfile}
                                onUpdateProfile={updateProfile}
                            />
                        )}

                        {activeTab === 'security' && (
                            <SecuritySection
                                onSignOut={handleSignOut}
                                onDeleteAccount={handleDeleteAccount}
                            />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
